/**
 * Parsers for the index-keyed per-option maps inside a question's `optionsJson`
 * (`durationExtensionMinutes`, `excludedUserIds`).
 *
 * Keep this module free of imports: `questionOptionsJson` depends on it and is
 * shared with the mobile app, whose `questionConditionalLogic` differs from this one.
 */

/** The increments offered in the builder. Anything else is treated as invalid. */
export const DURATION_EXTENSION_PRESETS = [15, 30, 45, 60] as const;

export type DurationExtensionPreset = (typeof DURATION_EXTENSION_PRESETS)[number];

export function isDurationExtensionPreset(value: unknown): value is DurationExtensionPreset {
  return typeof value === 'number' && (DURATION_EXTENSION_PRESETS as readonly number[]).includes(value);
}

/**
 * Parse the per-option extension map: option index (string key) → added minutes.
 * Entries with a non-preset value or an unparseable index are dropped.
 */
export function parseDurationExtensions(
  optionsJson: string | null | undefined
): Record<string, number> {
  if (!optionsJson) return {};
  try {
    const parsed = JSON.parse(optionsJson);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') return {};
    const raw = parsed.durationExtensionMinutes;
    if (!raw || typeof raw !== 'object') return {};
    const result: Record<string, number> = {};
    Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
      const idx = Number(key);
      if (!Number.isFinite(idx) || idx < 0) return;
      if (!isDurationExtensionPreset(value)) return;
      result[String(idx)] = value;
    });
    return result;
  } catch {
    return {};
  }
}

/** Drop blanks and repeats while keeping the operator's original order. */
export function normalizeUserIds(values: readonly unknown[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    if (typeof value !== 'string' || value.length === 0) continue;
    if (seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
}

/**
 * Parse the per-option exclusion map: option index (string key) → user ids.
 * Entries with an unparseable index or no usable ids are dropped.
 */
export function parseUserExclusions(
  optionsJson: string | null | undefined
): Record<string, string[]> {
  if (!optionsJson) return {};
  try {
    const parsed = JSON.parse(optionsJson);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') return {};
    const raw = parsed.excludedUserIds;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};

    const result: Record<string, string[]> = {};
    Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
      const idx = Number(key);
      if (!Number.isFinite(idx) || idx < 0) return;
      if (!Array.isArray(value)) return;
      const userIds = normalizeUserIds(value);
      if (userIds.length > 0) result[String(idx)] = userIds;
    });
    return result;
  } catch {
    return {};
  }
}
